import { Link, useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { EmptyState, InlineAlert, LoadingRows } from "@/components/admin/feedback";
import { PageFrame } from "@/components/admin/page-frame";
import { PageHeader } from "@/components/admin/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { apiErrorMessage } from "@/lib/api";
import { adminLicensesPath } from "@/lib/navigation";
import { useProductAnalysisQueries } from "@/lib/queries";
import { useTimezone } from "@/lib/timezone";

const segments = ["paid", "trial", "combined"] as const;
const windows = ["24h", "7d", "30d"] as const;

function AnalysisMeta({
  data,
  definition,
  formatDateTime,
}: {
  data: { generated_at: string; window_started_at: string } | undefined;
  definition: string;
  formatDateTime: (value: string) => string;
}) {
  return data ? (
    <p className="px-4 pb-3 text-xs leading-5 text-muted">
      {definition} {formatDateTime(data.window_started_at)} –{" "}
      {formatDateTime(data.generated_at)}.
    </p>
  ) : null;
}

export function ProductAnalysisPage() {
  const { productId = "" } = useParams();
  const [search, setSearch] = useSearchParams();
  const { t } = useTranslation();
  const { formatDateTime, timezoneAbbr } = useTimezone();
  const segment = (segments.includes(
    search.get("segment") as (typeof segments)[number],
  )
    ? search.get("segment")
    : "paid") as (typeof segments)[number];
  const window = (windows.includes(
    search.get("window") as (typeof windows)[number],
  )
    ? search.get("window")
    : "7d") as (typeof windows)[number];
  const queries = useProductAnalysisQueries(productId, segment, window);
  const setFilter = (key: "segment" | "window", value: string) =>
    setSearch({ segment, window, [key]: value });
  const failedCount = [
    queries.platform,
    queries.version,
    queries.trend,
  ].filter((item) => item.isError).length;
  const panelError = (
    query: { isError: boolean; error: unknown; refetch: () => unknown },
    name: string,
  ) =>
    query.isError ? (
      <InlineAlert
        title={name}
        message={apiErrorMessage(query.error)}
        onRetry={() => void query.refetch()}
      />
    ) : null;

  return (
    <PageFrame size="fluid" scrollable>
      <PageHeader
        tone="analytics"
        title={t("analysis.title", "Product analysis")}
        description={<span className="font-mono">{productId}</span>}
        actions={
          <>
            <ToggleGroup
              type="single"
              value={segment}
              onValueChange={(value) =>
                value && setFilter("segment", value)
              }
              aria-label={t("usage.segmentAria", "Product usage segment")}
            >
              {segments.map((value) => (
                <ToggleGroupItem key={value} value={value}>
                  {t(`usage.segments.${value}`, value)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <ToggleGroup
              type="single"
              value={window}
              onValueChange={(value) => value && setFilter("window", value)}
              aria-label={t(
                "usage.windowAria",
                "Recently Seen Device window",
              )}
            >
              {windows.map((value) => (
                <ToggleGroupItem key={value} value={value}>
                  {t(`usage.windows.${value}`, value)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </>
        }
      />

      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <Link
          className="text-sm text-accent underline-offset-4 hover:underline"
          to={`/admin/usage?segment=${encodeURIComponent(segment)}&window=${encodeURIComponent(window)}`}
        >
          {t("analysis.back", "Back to Product usage")}
        </Link>
        <nav
          aria-label={t("analysis.workflows", "Existing workflows")}
          className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm"
        >
          <span className="text-muted">
            {t("analysis.relatedRecords", "Related records")}
          </span>
          <Link
            className="text-accent underline-offset-4 hover:underline"
            to={adminLicensesPath({ product: productId })}
          >
            {t("usage.licenses", "Licenses")}
          </Link>
        </nav>
      </div>

      {failedCount > 0 ? (
        <div className="shrink-0">
          <InlineAlert
            title={
              failedCount === 3
                ? t("analysis.allFailed", "Product analysis could not load")
                : t("analysis.partial", "Some analysis could not load")
            }
            message={
              failedCount === 3
                ? t(
                    "analysis.allFailedHelp",
                    "Retry an analysis panel to load it.",
                  )
                : t(
                    "analysis.partialHelp",
                    "Other Product analysis remains available.",
                  )
            }
          />
        </div>
      ) : null}

      <section
        className="shrink-0 overflow-hidden rounded-xl bg-panel"
        data-analysis-panel="platform"
      >
        <h2 className="px-4 py-3 font-medium text-ink">
          {t("analysis.platforms", "Platform distribution")}
        </h2>
        <AnalysisMeta
          data={queries.platform.data}
          definition={t(
            "analysis.platformDefinition",
            "Recently Seen Devices by platform for",
          )}
          formatDateTime={formatDateTime}
        />
        {queries.platform.isPending ? (
          <LoadingRows />
        ) : (
          panelError(
            queries.platform,
            t(
              "analysis.platformError",
              "Could not load platform distribution",
            ),
          ) ||
          (queries.platform.data?.platforms.length === 0 ? (
            <EmptyState
              title={t(
                "analysis.platformEmpty",
                "No recently seen devices",
              )}
              message={t(
                "analysis.platformEmptyHelp",
                "No platform evidence in this window.",
              )}
            />
          ) : (
            <Table>
              <caption className="sr-only">
                {t("analysis.platforms", "Platform distribution")}
              </caption>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("analysis.platform", "Platform")}</TableHead>
                  <TableHead className="text-right">
                    {t("analysis.devices", "Recently Seen Devices")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queries.platform.data?.platforms.map((item) => (
                  <TableRow key={item.value}>
                    <TableCell>{item.value}</TableCell>
                    <TableCell className="text-right font-mono">
                      {item.count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ))
        )}
      </section>

      <section
        className="shrink-0 overflow-hidden rounded-xl bg-panel"
        data-analysis-panel="version"
      >
        <h2 className="px-4 py-3 font-medium text-ink">
          {t("analysis.versions", "App-version evidence")}
        </h2>
        <AnalysisMeta
          data={queries.version.data}
          definition={t(
            "analysis.versionDefinition",
            "Activation-time evidence is not a current report; window:",
          )}
          formatDateTime={formatDateTime}
        />
        {queries.version.isPending ? (
          <LoadingRows />
        ) : (
          panelError(
            queries.version,
            t(
              "analysis.versionError",
              "Could not load app-version evidence",
            ),
          ) ||
          (queries.version.data?.versions.length === 0 ? (
            <EmptyState
              title={t("analysis.versionEmpty", "No app-version evidence")}
              message={t(
                "analysis.versionEmptyHelp",
                "No recently seen device reported a version in this window.",
              )}
            />
          ) : (
            <Table>
              <caption className="sr-only">
                {t("analysis.versions", "App-version evidence")}
              </caption>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("analysis.version", "Version")}</TableHead>
                  <TableHead>{t("analysis.evidence", "Evidence")}</TableHead>
                  <TableHead>
                    {t("analysis.reported", "Latest report")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("analysis.devices", "Recently Seen Devices")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queries.version.data?.versions.map((item, index) => (
                  <TableRow
                    key={`${item.value}-${item.evidence_state}-${index}`}
                  >
                    <TableCell>{item.value}</TableCell>
                    <TableCell>
                      {t(
                        `analysis.states.${item.evidence_state}`,
                        item.evidence_state,
                      )}
                    </TableCell>
                    <TableCell>
                      {item.latest_reported_at
                        ? formatDateTime(item.latest_reported_at)
                        : t("analysis.noEvidence", "No version evidence")}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {item.count}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ))
        )}
      </section>

      <section
        className="shrink-0 overflow-hidden rounded-xl bg-panel"
        data-analysis-panel="lifecycle"
      >
        <h2 className="px-4 py-3 font-medium text-ink">
          {t("analysis.lifecycle", "Lifecycle events")}
        </h2>
        <AnalysisMeta
          data={queries.trend.data}
          definition={t(
            "analysis.trendDefinition",
            "Real Activation timestamps; window:",
          )}
          formatDateTime={formatDateTime}
        />
        {queries.trend.isPending ? (
          <LoadingRows />
        ) : (
          panelError(
            queries.trend,
            t("analysis.trendError", "Could not load lifecycle trend"),
          ) || (
            <Table>
              <caption className="sr-only">
                {t("analysis.lifecycle", "Lifecycle events")}
              </caption>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("analysis.bucket", {
                      tz: timezoneAbbr,
                      defaultValue: `Bucket (${timezoneAbbr}; calculated from UTC boundaries)`,
                    })}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("usage.lifecycleNew", "New")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("usage.lifecycleDeactivated", "Deactivated")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queries.trend.data?.buckets.map((item) => (
                  <TableRow key={item.started_at}>
                    <TableCell>
                      {formatDateTime(item.started_at)} –{" "}
                      {formatDateTime(item.ended_at)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {item.created}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {item.deactivated}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
        )}
      </section>
    </PageFrame>
  );
}
